import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Flag, FlagDefinitionDetail } from "@spoonity/flags-contract";
import { getDefinition, listFlags } from "../api/endpoints";
import { canWrite, useAuth } from "../auth/AuthContext";
import { EmptyState } from "../components/EmptyState";
import { LoadMore } from "../components/LoadMore";
import { useToast } from "../components/Toast";
import { useWorkspaceEnv } from "../state/WorkspaceEnvContext";

type StatusFilter = "all" | "enabled" | "killed" | "archived";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function FlagsListPage() {
  const { workspace, environment } = useWorkspaceEnv();
  const { identity } = useAuth();
  const writable = canWrite(identity?.role);
  const { pushError } = useToast();
  const [flags, setFlags] = useState<Flag[]>([]);
  const [definitions, setDefinitions] = useState<Map<string, FlagDefinitionDetail | null>>(new Map());
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");

  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    setLoading(true);
    setFlags([]);
    setDefinitions(new Map());
    (async () => {
      try {
        const page = await listFlags(workspace.id, {
          q: query || undefined,
          tag: tag || undefined,
          archived: status === "archived",
          limit: 50,
        });
        if (cancelled) return;
        setFlags(page.items);
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load flags");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspace, query, tag, status, pushError]);

  useEffect(() => {
    if (!environment || flags.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        flags
          .filter((f) => !definitions.has(f.key))
          .map(async (f) => [f.key, await getDefinition(environment.id, f.key)] as const),
      );
      if (cancelled) return;
      setDefinitions((cur) => {
        const next = new Map(cur);
        for (const r of results) {
          if (r.status === "fulfilled") next.set(r.value[0], r.value[1]);
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment, flags]);

  async function loadMore() {
    if (!workspace || !nextCursor) return;
    setLoading(true);
    try {
      const page = await listFlags(workspace.id, {
        q: query || undefined,
        tag: tag || undefined,
        archived: status === "archived",
        cursor: nextCursor,
        limit: 50,
      });
      setFlags((cur) => [...cur, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load flags");
    } finally {
      setLoading(false);
    }
  }

  const visible = useMemo(() => {
    if (status === "all" || status === "archived") return flags;
    return flags.filter((f) => {
      const def = definitions.get(f.key)?.definition;
      if (!def) return false;
      if (status === "killed") return def.killed;
      if (status === "enabled") return def.enabled && !def.killed;
      return true;
    });
  }, [flags, definitions, status]);

  if (!workspace) {
    return <EmptyState title="No workspace" message="Create a workspace to get started." />;
  }

  return (
    <div>
      <div className="row wrap" style={{ marginBottom: "var(--space-4)" }}>
        <input
          className="select"
          type="search"
          placeholder="Search flags…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search flags"
        />
        <input
          className="select"
          type="text"
          placeholder="Filter by tag"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          aria-label="Filter by tag"
        />
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="enabled">Enabled</option>
          <option value="killed">Killed</option>
          <option value="archived">Archived</option>
        </select>
        <div className="spacer" />
        {writable && (
          <Link className="btn primary" to="/flags/new">
            New flag
          </Link>
        )}
      </div>

      {visible.length === 0 && !loading ? (
        <EmptyState title="No flags found" message="Try a different search or create a new flag." />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Flag</th>
              <th>Status ({environment?.name ?? "…"})</th>
              <th>Tags</th>
              <th>Last change</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((f) => {
              const detail = definitions.get(f.key);
              const def = detail?.definition;
              const statusLabel = f.archived
                ? "Archived"
                : !def
                  ? "Not configured"
                  : def.killed
                    ? "Killed"
                    : def.enabled
                      ? "Enabled"
                      : "Disabled";
              const dotClass = def?.killed ? "killed" : def?.enabled ? "on" : "off";
              return (
                <tr key={f.key}>
                  <td>
                    <Link to={`/flags/${f.key}`}>{f.name}</Link>
                    <div className="muted">{f.key}</div>
                  </td>
                  <td>
                    <span className={`status-dot ${dotClass}`} />
                    {statusLabel}
                  </td>
                  <td>
                    {f.tags.map((t) => (
                      <span key={t} className="tag">
                        {t}
                      </span>
                    ))}
                  </td>
                  <td>{formatDate(detail?.updatedAt ?? f.updatedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <LoadMore nextCursor={nextCursor} loading={loading} onLoadMore={loadMore} />
    </div>
  );
}
